import { useState, useEffect } from 'react';
import { useSearch, Link } from 'wouter';
import { Plus } from 'lucide-react';
import { useReservations } from '@/lib/api';
import { UserAPI } from '@shared/types/api';
import { ReservationCard } from '../components/ReservationCard';
import { ConnectionsView } from '../components/ConnectionsView';
import ImageHeader from '../components/ux/ImageHeader';
import bookingsImg from '@/assets/images/img_bar.webp';
import connectionsImg from '@/assets/images/img_3.webp';
import dayjs from 'dayjs';
import { useT } from '@/lib/i18n';
import tracking from '@/lib/tracking';

type BookingTab = 'upcoming' | 'previous';

export default function Connections() {
  const { t } = useT();
  const searchString = useSearch();

  useEffect(() => { tracking.trackFlow('connections'); }, []);
  const params = new URLSearchParams(searchString);
  const tabParam = params.get('tab');
  const { data: reservationsData } = useReservations();
  const [bookingTab, setBookingTab] = useState<BookingTab>(
    tabParam === 'previous' ? 'previous' : 'upcoming',
  );

  const reservations = reservationsData?.data || [];

  // Separate reservations into upcoming and past
  const now = dayjs();
  const upcomingReservations = reservations.filter((r: UserAPI.Reservation) => {
    const eventDate = dayjs(r.localEventDate);
    return (
      eventDate.isAfter(now) &&
      (r.status === 'active' || r.status === 'cant_be_seated')
    );
  });

  const pastReservations = reservations.filter((r: UserAPI.Reservation) => {
    const eventDate = dayjs(r.localEventDate);
    return eventDate.isBefore(now) && r.status !== 'cancelled';
  });

  const activeList =
    bookingTab === 'upcoming' ? upcomingReservations : pastReservations;

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* My Connections */}
        <section id="my-connections" className="scroll-mt-20">
          <ImageHeader
            title={t("events.myConnections")}
            imageSrc={connectionsImg}
            className="mb-3"
          />
          <ConnectionsView />
        </section>

        {/* Bookings */}
        <ImageHeader
          title={t("events.upcomingDinners")}
          imageSrc={bookingsImg}
          className="mt-12 mb-3"
          right={
            <Link
              href="/booking"
              className="flex items-center whitespace-nowrap text-white text-sm font-medium px-3 py-1.5 rounded-full bg-black/80 hover:bg-black/75 backdrop-blur-md border border-white/15 transition-colors"
            >
              <Plus className="h-4 w-4 inline-block mr-1" />
              {t("events.bookAnother")}
            </Link>
          }
        />

        {/* Booking Tabs */}
        <div className="flex gap-2 mb-6 border-b border-divider">
          <button
            onClick={() => setBookingTab('upcoming')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              bookingTab === 'upcoming'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-dark-muted hover:text-dark-text'
            }`}
          >
            {t("events.upcomingCount", { count: upcomingReservations.length })}
          </button>
          <button
            onClick={() => setBookingTab('previous')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              bookingTab === 'previous'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-dark-muted hover:text-dark-text'
            }`}
          >
            {t("events.previousCount", { count: pastReservations.length })}
          </button>
        </div>

        {/* Booking List */}
        {activeList.length > 0 ? (
          <div className="space-y-4">
            {activeList.map((reservation: UserAPI.Reservation) => (
              <ReservationCard
                key={reservation.id}
                reservation={reservation}
              />
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <p className="text-dark-muted">
              {bookingTab === 'upcoming'
                ? t("events.noUpcomingBookings")
                : t("events.noPastBookings")}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
